import { z } from "@hono/zod-openapi";

export const motionAmplificationJobIdSchema = z
  .string()
  .uuid()
  .brand<"MotionAmplificationJobId">();
export type MotionAmplificationJobId = z.infer<
  typeof motionAmplificationJobIdSchema
>;

const externalCustomerIdSchema = z
  .string()
  .min(1)
  .max(45)
  .brand<"CustomerId">();
const externalFacilityIdSchema = z
  .string()
  .min(1)
  .max(45)
  .brand<"FacilityId">();
const externalMachineIdSchema = z.string().min(1).max(45).brand<"MachineId">();
export type MachineId = z.infer<typeof externalMachineIdSchema>;

export function asCustomerId(value: string) {
  return externalCustomerIdSchema.parse(value);
}

export function asFacilityId(value: string) {
  return externalFacilityIdSchema.parse(value);
}

export function asMachineId(value: string): MachineId {
  return externalMachineIdSchema.parse(value);
}

export function asMotionAmplificationJobId(
  value: string,
): MotionAmplificationJobId {
  return motionAmplificationJobIdSchema.parse(value);
}

export const jobIdParamSchema = z.object({
  jobId: motionAmplificationJobIdSchema.openapi({
    param: { name: "jobId", in: "path" },
    example: "3e7b4271-3a74-44f5-9011-c40bea3bf3e3",
  }),
});
export type JobIdParam = z.infer<typeof jobIdParamSchema>;

const customerIdParamSchema = z.object({
  customerId: externalCustomerIdSchema.openapi({
    param: { name: "customerId", in: "path" },
  }),
});

const facilityIdParamSchema = z.object({
  facilityId: externalFacilityIdSchema.openapi({
    param: { name: "facilityId", in: "path" },
  }),
});

export const motionAmplificationAssignmentSchema = z
  .object({
    customerId: externalCustomerIdSchema,
    customerName: z.string(),
    facilityId: externalFacilityIdSchema,
    facilityName: z.string(),
    lineName: z.string().nullable(),
    machineId: externalMachineIdSchema,
    machineName: z.string(),
  })
  .openapi("MotionAmplificationAssignment");

const assignmentOptionSchema = z
  .object({
    id: z.string().min(1).max(45),
    name: z.string(),
  })
  .openapi("MotionAmplificationAssignmentOption");
export type AssignmentOption = z.infer<typeof assignmentOptionSchema>;

export const assignmentOptionsSchema = z.object({
  options: z.array(assignmentOptionSchema),
});

export const jobsListQuerySchema = z.object({
  limit: z.coerce.number().int().positive().max(1000).default(50).openapi({
    example: 50,
  }),
  cursor: z.string().min(1).optional().openapi({
    description: "Opaque cursor from a previous jobs page.",
  }),
});
export type JobsListQuery = z.infer<typeof jobsListQuerySchema>;

export const motionAmplificationJobStatusSchema = z
  .enum(["UploadPending", "Processing", "Completed", "Failed"])
  .openapi("MotionAmplificationJobStatus");
export type MotionAmplificationJobStatus = z.infer<
  typeof motionAmplificationJobStatusSchema
>;

const cropSchema = z
  .object({
    x: z.number().int().nonnegative(),
    y: z.number().int().nonnegative(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  })
  .nullable();

export const motionAmplificationParameterOverridesSchema = z
  .object({
    frequencyLowHz: z.number().finite().min(0.5).max(1000).optional(),
    frequencyHighHz: z.number().finite().min(0.5).max(1000).optional(),
    alpha: z.number().finite().min(0).max(1000).optional(),
    lambdaC: z.number().finite().min(0).max(1000).optional(),
    stabilize: z.boolean().optional(),
    stabilizeSmooth: z.number().int().positive().max(1001).optional(),
    maxWidth: z.number().int().positive().max(4096).nullable().optional(),
    pyramidLevels: z.number().int().positive().max(12).optional(),
    chromAttenuation: z.number().finite().min(0).max(1).optional(),
    chunkSize: z.number().int().positive().max(2048).optional(),
    overlap: z.number().int().positive().nullable().optional(),
    stabilizeSmoothPasses: z.number().int().positive().max(10).optional(),
    stabilizeMaxFeatures: z.number().int().positive().max(5000).optional(),
    stabilizeRansacPx: z.number().finite().min(0.1).max(100).optional(),
    stabilizeFastThreshold: z.number().int().positive().max(255).optional(),
    crop: cropSchema.optional(),
    heatmap: z.boolean().optional(),
    sideBySide: z.boolean().optional(),
    heatmapAlpha: z.number().finite().min(0).max(1).optional(),
  })
  .strict()
  .openapi("MotionAmplificationParameterOverrides");
export type MotionAmplificationParameterOverrides = z.infer<
  typeof motionAmplificationParameterOverridesSchema
>;

export const motionAmplificationParametersSchema =
  motionAmplificationParameterOverridesSchema
    .extend({ fps: z.number().finite().min(0.001).max(1000).optional() })
    .openapi("MotionAmplificationParameters");

export const motionAmplificationJobSummarySchema = z
  .object({
    jobId: motionAmplificationJobIdSchema,
    name: z.string().nullable(),
    description: z.string().nullable(),
    fileName: z.string().nullable(),
    assignment: motionAmplificationAssignmentSchema.nullable().default(null),
    status: motionAmplificationJobStatusSchema,
    phase: z.string().nullable(),
    progressPercent: z.number().nullable(),
    errorMessage: z.string().nullable(),
    rawObjectKey: z.string().nullable(),
    processedObjectKey: z.string().nullable(),
    // Completed jobs include a short-lived URL so gallery cards can play the
    // rendered output without a separate request per card.
    processedVideoUrl: z.string().nullable(),
    preset: z.string().nullable(),
    parameters: motionAmplificationParametersSchema,
    dateCreated: z.string().datetime(),
    startedAt: z.string().datetime().nullable(),
    finishedAt: z.string().datetime().nullable(),
  })
  .openapi("MotionAmplificationJobSummary");
export type MotionAmplificationJobSummary = z.infer<
  typeof motionAmplificationJobSummarySchema
>;

export const motionAmplificationJobListSchema = z
  .object({
    jobs: z.array(motionAmplificationJobSummarySchema),
    nextCursor: z.string().nullable(),
  })
  .openapi("MotionAmplificationJobList");
export type MotionAmplificationJobList = z.infer<
  typeof motionAmplificationJobListSchema
>;

export const motionAmplificationJobDetailSchema =
  motionAmplificationJobSummarySchema
    .extend({
      rawBucketName: z.string().nullable(),
      processedBucketName: z.string().nullable(),
    })
    .openapi("MotionAmplificationJobDetail");
export type MotionAmplificationJobDetail = z.infer<
  typeof motionAmplificationJobDetailSchema
>;

export const motionAmplificationJobDownloadUrlsSchema = z
  .object({
    rawVideoUrl: z.string().nullable(),
    rawVideoDownloadUrl: z.string().nullable(),
    processedVideoDownloadUrl: z.string().nullable(),
  })
  .openapi("MotionAmplificationJobDownloadUrls");
export type MotionAmplificationJobDownloadUrls = z.infer<
  typeof motionAmplificationJobDownloadUrlsSchema
>;

export const updateMotionAmplificationJobRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    machineId: externalMachineIdSchema.nullable(),
  })
  .strict()
  .openapi("UpdateMotionAmplificationJobRequest");
export type UpdateMotionAmplificationJobRequest = z.infer<
  typeof updateMotionAmplificationJobRequestSchema
>;

export { customerIdParamSchema, facilityIdParamSchema };

export const motionAmplificationRawVideoSchema = z
  .object({
    fileName: z.string().nullable(),
    rawObjectKey: z.string(),
    fileSize: z.number(),
    contentType: z.string(),
    fps: z.number().nullable(),
    durationSeconds: z.number().positive().nullable(),
    width: z.number().int().positive().nullable().optional(),
    height: z.number().int().positive().nullable().optional(),
    dateCreated: z.string().datetime().nullable(),
  })
  .openapi("MotionAmplificationRawVideo");
export type MotionAmplificationRawVideo = z.infer<
  typeof motionAmplificationRawVideoSchema
>;

export const motionAmplificationRawVideoListSchema = z
  .object({ rawVideos: z.array(motionAmplificationRawVideoSchema) })
  .openapi("MotionAmplificationRawVideoList");

export const motionAmplificationPresetSchema = z
  .enum([
    "standard-60fps",
    "high-frequency-240fps",
    "high-frequency-120fps",
    "high-stability-review",
  ])
  .openapi("MotionAmplificationPreset");

const createUploadRequestBaseSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(10000).nullable().optional(),
  fileName: z.string().max(100).optional(),
  fileSize: z
    .number()
    .positive()
    .max(5 * 1024 * 1024 * 1024)
    .optional(),
  contentType: z
    .string()
    .regex(/^video\/[\w.+-]+$/i)
    .optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  rawObjectKey: z.string().max(1024).optional(),
  machineId: externalMachineIdSchema.nullable().optional(),
  preset: motionAmplificationPresetSchema,
  fps: z.number().positive().optional(),
  parameters: motionAmplificationParameterOverridesSchema,
});

export const createUploadRequestSchema = createUploadRequestBaseSchema
  .extend({ source: z.enum(["upload", "existing"]).optional() })
  .superRefine((input, context) => {
    if (input.source !== "existing") {
      if (!input.fileName) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["fileName"],
          message: "fileName is required when source is 'upload'.",
        });
      }
      if (input.fileSize === undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["fileSize"],
          message: "fileSize is required when source is 'upload'.",
        });
      }
      if (!input.contentType) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["contentType"],
          message: "contentType is required when source is 'upload'.",
        });
      }
    }
    if (input.source === "existing" && !input.rawObjectKey) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["rawObjectKey"],
        message: "rawObjectKey is required when source is 'existing'.",
      });
    }
    if ((input.width === undefined) !== (input.height === undefined)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["width"],
        message: "width and height must be provided together.",
      });
    }
  })
  .openapi("MotionAmplificationCreateUploadRequest");
export type CreateUploadRequest = z.infer<typeof createUploadRequestSchema>;

export const createUploadResponseSchema = z
  .object({
    jobId: motionAmplificationJobIdSchema,
    bucketName: z.string().nullable(),
    objectKey: z.string(),
    uploadUrl: z.string().nullable(),
    expiresInSeconds: z.number(),
    source: z.enum(["upload", "existing"]),
  })
  .openapi("MotionAmplificationCreateUploadResponse");
export type CreateUploadResponse = z.infer<typeof createUploadResponseSchema>;

export const startJobResponseSchema = z
  .object({
    jobId: motionAmplificationJobIdSchema,
    status: z.enum(["Processing"]),
    phase: z.string().nullable().optional(),
    progressPercent: z.number().nullable().optional(),
    processedBucketName: z.string().nullable().optional(),
    outputObjectKey: z.string().nullable().optional(),
  })
  .openapi("MotionAmplificationStartJobResponse");
export type StartJobResponse = z.infer<typeof startJobResponseSchema>;

export const reportJobFailureRequestSchema = z
  .object({
    phase: z.string().max(64).optional(),
    errorMessage: z.string().max(2000).optional(),
  })
  .openapi("MotionAmplificationReportJobFailureRequest");
export type ReportJobFailureRequest = z.infer<
  typeof reportJobFailureRequestSchema
>;

export const reportJobFailureResponseSchema = z
  .object({
    jobId: motionAmplificationJobIdSchema,
    status: z.enum(["Completed", "Failed"]),
  })
  .openapi("MotionAmplificationReportJobFailureResponse");
export type ReportJobFailureResponse = z.infer<
  typeof reportJobFailureResponseSchema
>;

export type MotionAmplificationPreset =
  | "standard-60fps"
  | "high-frequency-240fps"
  | "high-frequency-120fps"
  | "high-stability-review";
